import React from 'react'
import Preview from '@renderer/components/Preview'
import type { FrameOption } from '@renderer/types/configState'
import type { FilterAdjustments } from '@renderer/utils/filterUtils'
import { thumbnailShowsWholeCanvas } from '@renderer/utils/frameThumbnail'
import type { FilterOverlayImage } from '@renderer/utils/lutUtils'
import { getPlaceholderClipCss } from '@frameShapes'
import type { RawSlotMeta } from '@frameShapes'
import { FaUndo } from 'react-icons/fa'
import type { PreviewCaptureShareGroupId } from '@renderer/utils/previewCaptureShare'
import { frameImageLayerStyle, rotationRadToCssDeg } from '@renderer/components/FrameCanvasBackdrop'

export type FrameDrawImage = FrameOption['drawParams']['images'][number]

export interface FrameOrderedLiveLayersProps {
  drawParamsImages: FrameDrawImage[]
  thumbnailUrl: string
  /** The frame's `thumbnailSource`: the thumbnail is a backdrop only when it is the whole canvas. */
  thumbnailSource: FrameOption['thumbnailSource']
  frameBackgroundColor: string
  scale: number
  /** e.g. landscape right strip uses 0.95 so layout matches existing FrameBasedCapture */
  scaleMultiplier?: number
  uniquePhotoTypesArray: string[]
  capturedPhotosByType: Map<string, string>
  filterAdjustments?: FilterAdjustments
  selectedFilterId?: string
  postLUTAdjustments?: {
    vignette?: number
    grain?: number
    blur?: number
    clarity?: number
    sharpeningAmount?: number
    bloom?: number
    softFocus?: number
    halation?: number
  }
  /** The selected filter's overlay layer; forwarded to every live slot Preview. */
  filterOverlay?: FilterOverlayImage | null
  cameraKey: 'primary' | 'secondary'
  getPhotoRatio: (slot: FrameDrawImage) => number
  /** FrameBasedCapture: live preview follows currentPhotoTypeIndex */
  captureFlow: boolean
  currentPhotoTypeIndex: number
  /** Retake page: when non-null, that photo type shows live preview (first slot of type only) */
  retakingPhotoType?: string | null
  showRetakeButtons?: boolean
  onRetakePhoto?: (photoType: string) => void
  isSharedCountdownExpired?: boolean
  capturing?: boolean
  /** Pause nested Preview streams during camera recovery */
  livePreviewPaused?: boolean
  /** L2 sensor crop; must match main Preview so slot `fillContainer` matches placeholder aspect */
  cameraRatio?: number
  /** Forward to live Preview — rotation/mirror baked for per-slot recorders */
  onRecordingCanvasReady?: (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    dimensions: { width: number; height: number }
  ) => void
  /** When set with a main Preview producer, strip slots reuse one camera capture per frame. */
  captureShareRole?: 'producer' | 'consumer'
  captureShareGroupId?: PreviewCaptureShareGroupId
}

/**
 * Renders frame strip layers in the same order as PrintPreview / drawParams.images (back → front).
 * Static `image` and `qr` layers are lightweight DOM nodes; only photo slots mount a Preview when live.
 */
const FrameOrderedLiveLayers: React.FC<FrameOrderedLiveLayersProps> = ({
  drawParamsImages,
  thumbnailUrl,
  thumbnailSource,
  frameBackgroundColor,
  scale,
  scaleMultiplier = 1,
  uniquePhotoTypesArray,
  capturedPhotosByType,
  filterAdjustments,
  selectedFilterId,
  postLUTAdjustments,
  filterOverlay,
  cameraKey,
  getPhotoRatio,
  captureFlow,
  currentPhotoTypeIndex,
  retakingPhotoType = null,
  showRetakeButtons = false,
  onRetakePhoto,
  isSharedCountdownExpired = false,
  capturing = false,
  livePreviewPaused = false,
  cameraRatio,
  onRecordingCanvasReady,
  captureShareRole,
  captureShareGroupId
}) => {
  const sm = scale * scaleMultiplier
  const hasStructuredImageLayers = drawParamsImages.some((img) => img.type === 'image' && img.src)
  // A frame with no image layer is drawn over its thumbnail — but only a
  // whole-frame thumbnail is that canvas. A strip or an upload stretched over it
  // would sit misaligned behind the slots; the background colour is what prints.
  const showThumbnailBackdrop =
    !hasStructuredImageLayers && thumbnailShowsWholeCanvas(thumbnailSource)

  const currentPhotoType = uniquePhotoTypesArray[currentPhotoTypeIndex] ?? ''

  return (
    <>
      {showThumbnailBackdrop && (
        <img
          src={thumbnailUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          crossOrigin="anonymous"
        />
      )}

      {!showThumbnailBackdrop && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundColor: frameBackgroundColor }}
        />
      )}

      {drawParamsImages.map((layer, i) => {
        const key = `frame-layer-${i}-${layer.type}`

        if (layer.type === 'image' && layer.src) {
          return (
            <img
              key={key}
              src={layer.src}
              alt=""
              className="absolute pointer-events-none"
              crossOrigin="anonymous"
              style={frameImageLayerStyle(layer, sm)}
            />
          )
        }

        if (layer.type === 'qr') {
          return null
        }

        if (layer.type.startsWith('photo-')) {
          const slot = layer
          const slotRot = rotationRadToCssDeg(slot.rotation)
          const isFirstSlotOfType = drawParamsImages.findIndex((img) => img.type === slot.type) === i
          const isCurrentSlot = slot.type === currentPhotoType
          const showLivePreview = captureFlow && isCurrentSlot && isFirstSlotOfType
          const showRetakeLive =
            !captureFlow &&
            retakingPhotoType != null &&
            retakingPhotoType === slot.type &&
            isFirstSlotOfType

          const capturedPhotoUrl = capturedPhotosByType.get(slot.type)
          const slotMeta = (slot as { slotMeta?: RawSlotMeta }).slotMeta
          const clipCss = getPlaceholderClipCss(slot.width, slot.height, slotMeta)

          return (
            <div
              key={key}
              className="absolute overflow-hidden"
              style={{
                left: `${slot.left * sm}px`,
                top: `${slot.top * sm}px`,
                width: `${slot.width * sm}px`,
                height: `${slot.height * sm}px`,
                transform: slotRot ? `rotate(${slotRot})` : undefined,
                transformOrigin: 'center center',
                ...(clipCss ?? {})
              }}
            >
              {showRetakeLive ? (
                <div className="w-full h-full relative overflow-hidden">
                  <Preview
                    adjustments={filterAdjustments}
                    ratio={getPhotoRatio(slot)}
                    cameraRatio={cameraRatio}
                    cameraKey={cameraKey}
                    filter={selectedFilterId}
                    postLUTAdjustments={postLUTAdjustments}
                    filterOverlay={filterOverlay}
                    fillContainer={true}
                    isPaused={livePreviewPaused}
                    onRecordingCanvasReady={onRecordingCanvasReady}
                    captureShareRole={captureShareRole}
                    captureShareGroupId={captureShareGroupId}
                  />
                </div>
              ) : capturedPhotoUrl ? (
                <>
                  <img
                    src={capturedPhotoUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    crossOrigin="anonymous"
                  />
                  {showRetakeButtons &&
                    !capturing &&
                    !isSharedCountdownExpired &&
                    isFirstSlotOfType &&
                    onRetakePhoto && (
                      <button
                        type="button"
                        onClick={() => onRetakePhoto(slot.type)}
                        className="absolute top-2 right-2 w-12 h-12 flex items-center justify-center hover:opacity-90 transition-all z-10 rounded-full shadow-lg bg-black/50"
                        style={{ color: '#FFFFFF' }}
                        aria-label={`Retake photo ${slot.type}`}
                      >
                        <FaUndo style={{ fontSize: '1.25rem' }} />
                      </button>
                    )}
                </>
              ) : showLivePreview ? (
                <div className="w-full h-full relative overflow-hidden">
                  <Preview
                    adjustments={filterAdjustments}
                    ratio={getPhotoRatio(slot)}
                    cameraRatio={cameraRatio}
                    cameraKey={cameraKey}
                    filter={selectedFilterId}
                    postLUTAdjustments={postLUTAdjustments}
                    filterOverlay={filterOverlay}
                    fillContainer={true}
                    isPaused={livePreviewPaused}
                    onRecordingCanvasReady={onRecordingCanvasReady}
                    captureShareRole={captureShareRole}
                    captureShareGroupId={captureShareGroupId}
                  />
                </div>
              ) : (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                  <span className="text-gray-400 text-2xl">
                    {uniquePhotoTypesArray.indexOf(slot.type) + 1}
                  </span>
                </div>
              )}
            </div>
          )
        }

        return null
      })}
    </>
  )
}

export default FrameOrderedLiveLayers
